import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { ApplicationEventResource } from '~/generated';
import Typography from '@mui/material/Typography';
import BlockIcon from '@mui/icons-material/Block';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { grey } from '@mui/material/colors';
import ApplyForShiftButton from '../ApplyForShiftButton';
import Event from '../Event';
import RevokeShiftApplicationButton from '../RevokeShiftApplicationButton';
import { FROZEN } from '../ShiftDetailCardApplicationStatus/constants';
import { ShiftApplicationRecommendationMessage } from '../ShiftApplicationRecommendationMessage';
import messages from './messages';
import styles from './styles.module.css';

export const APPLICATION_STATUS_APPLIED = 'Applied';
export const APPLICATION_STATUS_NOT_APPLIED = 'NotApplied';
export const APPLICATION_STATUS_ASSIGNED = 'Assigned';
export const APPLICATION_STATUS_CANCELED = 'Canceled';

const getEventTheme = ({
  publisherIsApplied,
  publisherIsAccepted,
  isCanceled,
}: {
  publisherIsApplied: boolean;
  publisherIsAccepted: boolean;
  isCanceled: boolean;
}) => {
  if (publisherIsAccepted && isCanceled) {
    return 'red';
  }
  if (publisherIsAccepted) {
    return 'green';
  }
  if (publisherIsApplied) {
    return 'green-striped';
  }
  return 'gray';
};

type ShiftApplicationEventProps = {
  applicationEvent: ApplicationEventResource;
  isHighlighted: boolean;
  isDimmed: boolean;
  onSelect: () => void;
  publisher: {
    isShiftLeader: boolean;
    gender: string;
  };
  isVehicleWorkflowActive: boolean;
};

export const ShiftApplicationEvent = (props: ShiftApplicationEventProps) => {
  if (
    !props.applicationEvent.shift?.startDate ||
    !props.applicationEvent.shift?.endDate ||
    !props.applicationEvent.shift?.id
  ) {
    return null;
  }
  const shiftId = props.applicationEvent.shift.id;
  const todayDay = moment().startOf('day');
  const eventDay = moment(props.applicationEvent.shift.startDate).startOf(
    'day'
  );
  const isPastEvent =
    eventDay.diff(todayDay, 'days') <= 0 &&
    todayDay.format('L') !== eventDay.format('L');

  const turnIsCanceled =
    props.applicationEvent.status === APPLICATION_STATUS_CANCELED;
  const publisherIsAssigned =
    props.applicationEvent.status === APPLICATION_STATUS_ASSIGNED;
  const publisherIsApplied =
    props.applicationEvent.status === APPLICATION_STATUS_APPLIED;
  const isShiftFrozen =
    isPastEvent || props.applicationEvent.shift.applicationState === FROZEN;

  return (
    <Event
      startDate={props.applicationEvent.shift.startDate}
      endDate={props.applicationEvent.shift.endDate}
      theme={getEventTheme({
        isCanceled: turnIsCanceled,
        publisherIsApplied,
        publisherIsAccepted: publisherIsAssigned,
      })}
      isDimmed={props.isDimmed}
      isHighlighted={props.isHighlighted}
      onClick={() => {
        if (!publisherIsAssigned) {
          return;
        }
        props.onSelect();
      }}
    >
      <div className={styles.event}>
        <div>
          {!publisherIsApplied ? (
            <Typography variant="body2" color="textSecondary" align="left">
              <ShiftApplicationRecommendationMessage
                publisher={props.publisher}
                hasAppliedTeamLead={
                  props.applicationEvent.hasAppliedTeamLead || false
                }
                numberOfMissingApplicationsForOneRouteToTakePlace={
                  props.applicationEvent
                    .numberOfMissingApplicationsForOneRouteToTakePlace || 0
                }
                numberOfMissingBrothersForOneRouteToTakePlace={
                  props.applicationEvent
                    .numberOfMissingBrothersForOneRouteToTakePlace || 0
                }
              />
            </Typography>
          ) : null}
        </div>
        <div className={styles.footer}>
          {isShiftFrozen && (
            <div className={styles.icon}>
              <BlockIcon style={{ color: grey[700] }} />
            </div>
          )}
          <div className={styles['bottom-right']}>
            {!isShiftFrozen && !publisherIsApplied && !publisherIsAssigned ? (
              <ApplyForShiftButton
                shiftId={shiftId}
                isVehicleWorkflowActive={props.isVehicleWorkflowActive}
                isPlanningCompleted={
                  props.applicationEvent.shift?.isPlanningCompleted || false
                }
              />
            ) : null}
            {!isShiftFrozen &&
            props.applicationEvent.application?.id &&
            !publisherIsAssigned ? (
              props.applicationEvent.application.canBeRevoked === false ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={styles.planning}
                >
                  <ScheduleIcon
                    sx={{ fontSize: 15, verticalAlign: 'text-bottom', mr: 0.5 }}
                  />
                  <FormattedMessage {...messages.planningInProgress} />
                </Typography>
              ) : (
                <RevokeShiftApplicationButton
                  shiftId={props.applicationEvent.shift.id}
                  applicationId={props.applicationEvent.application.id}
                />
              )
            ) : null}
          </div>
        </div>
      </div>
    </Event>
  );
};
